import {
  gql,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  useQuery,
} from '@apollo/client';
import { DetectionAlertGroupCountSummary } from '@investigator/shared/generated/graphql';

/**
 * Response type for entity timeline summary query
 * Fetches both source and destination timeline data for a specific entity
 */
export interface IGetEntityTimelineSummary {
  getCurrentDetectionTimelineSummary?: DetectionAlertGroupCountSummary; // ← Optional (not fetched for entities)
  getDetectionSourceTimelineSummary: DetectionAlertGroupCountSummary;
  getDetectionDestinationTimelineSummary: DetectionAlertGroupCountSummary;
  getDetectionSourceDestinationTimelineSummary?: DetectionAlertGroupCountSummary; // ← Optional
}

/**
 * GraphQL query for fetching entity-specific detection timeline
 *
 * This query fetches both source and destination timeline summaries
 * for a specific entity, used in the Entity Analysis Modal's Detections Activity view.
 *
 * Unlike the standard detection timeline queries that require detection_id,
 * this query filters by entity_name and is designed for entity-centric views.
 *
 * @see DetectionsActivity component for usage
 */
export const GET_ENTITY_DETECTION_TIMELINE_SUMMARY = gql`
  query getEntityDetectionTimelineSummary(
    $entity_name: String
    $detection_tenant: String
    $start: Float
    $end: Float
    $interval: Float
    $top_n: Int
    $alert_name_filter: [AlertNameFilterInput]
    $severity_filter: [DetectionAlertSummarySeverities]
  ) {
    getDetectionDestinationTimelineSummary(
      entity_name: $entity_name
      detection_tenant: $detection_tenant
      end: $end
      interval: $interval
      start: $start
      top_n: $top_n
      severity_filter: $severity_filter
      alert_name_filter: $alert_name_filter
    ) {
      row_count
      summary {
        alert_name
        detections {
          severity
          alert_name
          entity_name
          buckets {
            alert_count
            bucket_end
            bucket_start
            score
          }
          detection_id
          latest_start_timestamp
          earliest_start_timestamp
        }
        entity_name
        severity
        tenant
      }
    }
    getDetectionSourceTimelineSummary(
      entity_name: $entity_name
      detection_tenant: $detection_tenant
      end: $end
      interval: $interval
      start: $start
      top_n: $top_n
      severity_filter: $severity_filter
      alert_name_filter: $alert_name_filter
    ) {
      row_count
      summary {
        alert_name
        detections {
          severity
          alert_name
          entity_name
          buckets {
            alert_count
            bucket_end
            bucket_start
            score
          }
          detection_id
          latest_start_timestamp
          earliest_start_timestamp
        }
        entity_name
        severity
        tenant
      }
    }
  }
`;

export const useGetEntityTimelineSummary = (
  options: QueryHookOptions<IGetEntityTimelineSummary, OperationVariables>,
): QueryResult<IGetEntityTimelineSummary, OperationVariables> => {
  return useQuery<IGetEntityTimelineSummary>(
    GET_ENTITY_DETECTION_TIMELINE_SUMMARY,
    options,
  );
};

/**
 * Hook for querying entity detection timeline summary
 *
 * @example
 *
 * const { data, loading, error } = useGetEntityTimelineSummary({
 *   skip: !entityName || !tenantId,
 *   fetchPolicy: 'cache-and-network',
 *   variables: {
 *     entity_name: 'example.com',
 *     detection_tenant: 'tenant-123',
 *     start: startTimestamp,
 *     end: endTimestamp,
 *     interval: 3600,
 *     top_n: 5,
 *     severity_filter: undefined,
 *     alert_name_filter: undefined,
 *   },
 * });
 *
 * // Access timeline data
 * const sourceTimeline = data?.getDetectionSourceTimelineSummary;
 * const destTimeline = data?.getDetectionDestinationTimelineSummary;
 * */
